import { useSyncExternalStore } from 'react'

/**
 * A once-per-second clock for ticking UI (countdowns, call timers), the
 * repo's alternative to `useEffect + setInterval + useState`. One shared
 * interval feeds every subscriber; it stops when the last one unsubscribes.
 * `initialValue` is the server snapshot (the loader's `now`), so SSR and
 * hydration agree.
 */

let cachedNow = Date.now()
const listeners = new Set<() => void>()
let interval: ReturnType<typeof setInterval> | null = null

function subscribe(callback: () => void) {
	listeners.add(callback)
	if (!interval) {
		// The clock stood still while nobody listened: catch up right away so
		// the first subscriber doesn't show a stale second before the first tick.
		cachedNow = Date.now()
		callback()
		interval = setInterval(() => {
			cachedNow = Date.now()
			listeners.forEach((listener) => listener())
		}, 1000)
	}
	return () => {
		listeners.delete(callback)
		if (listeners.size === 0 && interval) {
			clearInterval(interval)
			interval = null
		}
	}
}

export function useNow(initialValue: number) {
	return useSyncExternalStore(
		subscribe,
		() => cachedNow,
		() => initialValue,
	)
}
